'use client';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Button } from '@ahafood/ui/components/button';
import { Separator } from '@ahafood/ui/components/separator';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { cn, JSFunction } from '@ahm/common-helpers';
import { ArrowLeft, Search } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const AiPlannerTrigger = dynamic(
  () => import('../ai-planner/plan-overview/ai-planner-trigger'),
  { ssr: false }
);
import { useGetFoodOrderCategories } from '@/api/ahafood/get-category-fn';
import { Link, useRouter } from '@/i18n/routing';
import { clearDebugLogs } from '@/lib/debug/use-debug-tracker';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import AiIcon from '@/public/static/icons/ai-star.svg';
import { useAuthStore } from '@/store';

type StoreCategoryProps = {
  city: string;
  initialData?: any[]; // Server-provided category data
};

export default function NewStoreCategory({
  city,
  initialData,
}: StoreCategoryProps) {
  const isMobile = useIsMobile();
  const { user } = useAuthStore();
  const router = useRouter();
  const isWebInApp = useDetectWebInApp();

  // Fetch categories using hook with server-provided initialData
  const { data: categoriesData, isLoading } = useGetFoodOrderCategories(
    {
      skip: 0,
      limit: 24,
    },
    {
      initialData: initialData ? { categories: initialData } : undefined,
    }
  );

  const data = categoriesData?.categories;
  const containerRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [containerHeight, setContainerHeight] = useState(0);
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    const handleScroll = () => {
      // Disable sticky on desktop
      if (!isMobile) {
        setIsSticky(false);
        return;
      }

      // Use placeholder position to determine if should be sticky
      const targetElement = isSticky
        ? placeholderRef.current
        : containerRef.current;

      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        // When the element reaches the top of the viewport
        const shouldBeSticky = rect.top <= 0;
        setIsSticky(shouldBeSticky);

        // Measure height for placeholder
        if (!containerHeight && containerRef.current) {
          setContainerHeight(containerRef.current.offsetHeight);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [containerHeight, isSticky]);

  const handleInputClick = useCallback(() => {
    logSearchBarClick();
    router.push(`/${city}/danh-sach-cua-hang?focus=1`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, city]);

  const handleBackClick = useCallback(() => {
    clearDebugLogs();
    JSFunction.call({ name: 'close' });
  }, []);

  const handleAiClick = () => {
    if (
      typeof window !== 'undefined' &&
      typeof window.setOpenChange === 'function'
    ) {
      window.setOpenChange(true);
      trackEvent('chatbot_event', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'chatbot_banner',
        timestamp: Date.now(),
        user_id: user?.cunstomerId,
      });
    }
  };

  const handleCategoryClick = useCallback(
    (slug: string) => {
      trackEvent('click_button', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'category_toggle',
        selected_category: slug,
        user_id: user?.cunstomerId,
      });
    },
    [user?.cunstomerId]
  );

  const logSearchBarClick = useCallback(() => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'search_bar',
      button_location: 'home',
    });
  }, []);

  const categoryItems = useMemo(() => {
    if (!data) return null;
    const allCategory = {
      id: 'all',
      slug: '',
      title_vi_vn: 'Tất cả',
      icon_img_url: '/static/images/all-category.webp',
    };
    const categories = [allCategory, ...data];
    return categories.map((c) => (
      <Link
        key={c.id}
        href={`/${city}/danh-sach-cua-hang-${c.slug}`}
        onClick={() => handleCategoryClick(c.slug)}
        className="flex flex-col items-center gap-2 shrink-0 w-16 lg:w-full group"
        data-testid="home-category-item"
      >
        <div className="relative aspect-square w-full lg:w-20 flex-none overflow-hidden rounded-full transition-all hover:scale-105 hover:shadow-md md:bg-white/20 md:border md:border-white/50 p-2">
          <Avatar className="scale-110 overflow-hidden rounded-full size-full">
            <AvatarImage
              src={c.icon_img_url || '/static/images/fastfood-logo.webp'}
              alt={c.slug}
              style={{ objectFit: 'cover' }}
              className="size-full scale-110"
              data-testid="home-category-icon"
            />
            <AvatarFallback className="size-full">
              {c.title_vi_vn ? c.title_vi_vn.charAt(0) : 'C'}
            </AvatarFallback>
          </Avatar>
        </div>

        {/* Category Name */}
        <span className="text-xs text-neutral-70 text-center leading-tight max-w-full line-clamp-2 font-medium group-hover:text-primary-50 transition-colors">
          {c.title_vi_vn}
        </span>
      </Link>
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, data, isMobile]);

  if (isLoading) {
    return (
      <>
        <div className="w-full flex items-center justify-center px-4 py-6 text-neutral-50 gap-10 bg-transparent">
          <Separator className="h-px w-full flex-1" />
          <span className="font-semibold"> Hôm nay ăn gì?</span>
          <Separator className="h-px w-full flex-1" />
        </div>

        <div className="relative bg-transparent">
          {/* Search bar container */}
          <div className="flex items-center gap-3 px-4 py-3">
            {/* Search input skeleton */}
            <div className="flex-1 relative">
              <Skeleton className="h-12 w-full rounded-full" />
            </div>

            {/* AI button skeleton */}
            <Skeleton className="rounded-full size-12" />
          </div>

          {/* Categories section */}
          <div className="px-4 pb-4">
            <div className="flex gap-4 overflow-x-auto scrollbar-hide -mx-4 px-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center gap-2 shrink-0 w-20"
                >
                  <Skeleton className="aspect-square w-full rounded-full" />
                  <Skeleton className="h-4 w-12 rounded" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  if (data?.length === 0 || !data) return null;

  return (
    <>
      <div className="w-full flex items-center justify-center px-4 py-6 text-neutral-50 gap-10 bg-transparent">
        <Separator className="h-px w-full flex-1" />
        <span className="font-semibold"> Hôm nay ăn gì?</span>
        <Separator className="h-px w-full flex-1" />
      </div>

      {isAuthenticated ? (
        <div className="w-full flex items-center justify-center px-4 mb-6">
          <AiPlannerTrigger />
        </div>
      ) : null}
      {/* Placeholder to prevent layout shift when fixed */}
      {isSticky && (
        <div ref={placeholderRef} style={{ height: containerHeight }} />
      )}

      <div
        ref={containerRef}
        className={cn(
          'transition-all duration-200',
          'lg:static lg:z-auto lg:bg-transparent lg:shadow-none',
          isSticky && isMobile
            ? 'fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-white via-white/95 to-white/90 shadow-md w-full max-w-screen-xl mx-auto rounded-b-3xl'
            : 'relative bg-transparent'
        )}
      >
        {/* Search bar container - Mobile only */}
        <div className="flex items-center gap-3 px-4 py-3 lg:hidden">
          {/* Back button */}
          {isSticky && isWebInApp ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackClick}
              className="shrink-0 size-auto p-0 rounded-full hover:bg-neutral-10 transition-colors [&_svg]:size-6"
            >
              <ArrowLeft className="text-neutral-70" />
            </Button>
          ) : null}
          {/* Search input container */}
          <div className="flex-1 relative">
            <button onClick={handleInputClick} className="aha-search-pill h-12">
              <Search className="aha-search-pill__icon size-6" />
              <span className="aha-search-pill__placeholder">
                Tìm tên món, tên quán
              </span>
            </button>
          </div>

          {/* AI button */}
          <Button
            variant="ghost"
            onClick={handleAiClick}
            className="aha-round-action rounded-full size-12 p-2 [&_svg]:size-6 bg-white"
          >
            <AiIcon className="size-6" />
          </Button>
        </div>

        {/* Categories section */}
        {data && data.length > 0 && (
          <div className="px-4 pb-4" data-testid="home-category-list">
            {/* Desktop: Grid layout */}
            <div className="hidden lg:grid lg:grid-cols-6 xl:grid-cols-8 gap-6">
              {categoryItems}
            </div>
          </div>
        )}
      </div>

      {/* Categories section - now outside of sticky container */}
      {data && data.length > 0 && (
        <div className="px-4 pb-4 bg-transparent md:hidden">
          <div className="flex gap-4 overflow-x-auto scrollbar-hide -mx-4 px-4">
            {categoryItems}
          </div>
        </div>
      )}
    </>
  );
}
